// What each load board counts as a "filter", declared once.
//
// A filter is three things — the slice field it lives in, how to validate it coming back out of
// localStorage, and which action re-applies it — and they have to agree. Keeping them on one line
// per filter is what stops them drifting apart; boardFilterSync then drives both saving and
// restoring off these tables without knowing what any individual filter means.
//
// Validation is per field on purpose: a value that no longer parses (a renamed enum, a hand-edited
// entry, a field added since the save) is dropped and that one filter falls back to the board
// default, instead of one bad field discarding the whole saved set.

import { DriverFilter, LoadFinanceStatus, SchedulerQuery, StopTypeFilter } from '@numeo/types-core'
import { toTripDateBasis } from '@numeo/types-main-web-app'
import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { isRecord } from 'utils/isRecord'
import loadsReducer, {
    setAllTripsDateRange,
    setAttentionNeededFilter,
    setBrokerFilter,
    setBrokerFilterMode,
    setDispatcherFilter,
    setDriverFilter,
    setFinanceStatusFilter,
    setLaneFilterMode,
    setSearchQuery,
    setSelectedLoadGroupId,
    setStopTypeFilter,
    setTagFilter,
    setUnassignedRiskFilter,
} from './loadsSlice'
import type { AllTripsDateRange } from './loadsSlice'
import schedulerReducer, {
    SCHEDULER_PAGE_SIZES,
    setDispatcherFilterInitialized,
    setDriverFilter as setBoardDriverFilter,
    setDriverStatusFilter,
    setRCFilter,
    setSearch,
    setViewPagination,
    type SchedulerPageSize,
} from './schedulerSlice'
import type { RootState } from './index'

/** The slices the boards keep their filters in. Narrower than RootState so a test can drive this with a small store. */
export type BoardFilterState = Pick<RootState, 'loads' | 'scheduler'>

export interface BoardFilterField {
    /** Doubles as the name it is stored under — the slice field name, so the two can't disagree. */
    name: string
    read: (state: BoardFilterState) => unknown
    /**
     * Puts this filter into the state the stored entry describes — the saved value, or the board
     * default when nothing valid was saved. Deliberately total rather than "apply what we have":
     * the store outlives a logout and a company switch, so a filter nobody saved has to be actively
     * cleared, or the next person on this browser inherits the last one's.
     */
    restore: (stored: unknown, dispatch: Dispatch) => void
}

export interface BoardFilterSpec {
    /** localStorage key prefix; the signed-in project name is appended to it. */
    storageKeyPrefix: string
    fields: BoardFilterField[]
}

/**
 * Builds the one-liners used in the tables below: `<board>Filter(name, parse, apply)`.
 *
 * `apply` is told whether the value came from storage (`fromStorage`) or is the board default
 * standing in for it, which is what lets the dispatch board tell "this dispatcher chose this" from
 * "nobody chose anything" — see its `search` entry.
 *
 * The action creators are called inside `apply` rather than passed by reference because loadsSlice
 * is annotated `Slice<LoadsState>`, which erases its creators' payload types — they type-check on
 * call but not as function values.
 */
const filterOn =
    <TSlice>(readSlice: (state: BoardFilterState) => TSlice) =>
    <TKey extends keyof TSlice & string, TValue extends TSlice[TKey]>(
        name: TKey,
        parse: (stored: unknown) => TValue | undefined,
        apply: (value: TValue, fromStorage: boolean) => UnknownAction | UnknownAction[]
    ): BoardFilterField => ({
        name,
        read: (state) => readSlice(state)[name],
        restore: (stored, dispatch) => {
            const saved = parse(stored)
            const value = saved !== undefined ? saved : (readSlice(DEFAULT_BOARD_STATE)[name] as TValue)
            const actions = apply(value, saved !== undefined)
            ;(Array.isArray(actions) ? actions : [actions]).forEach(dispatch)
        },
    })

const loadsFilter = filterOn((state: BoardFilterState) => state.loads)
const boardFilter = filterOn((state: BoardFilterState) => state.scheduler)

// ── Parsers ──────────────────────────────────────────────────────────────────

const isStringList = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === 'string')

const isNumberList = (value: unknown): value is number[] => Array.isArray(value) && value.every((item) => typeof item === 'number' && Number.isFinite(item))

const asString = (stored: unknown): string | undefined => (typeof stored === 'string' ? stored : undefined)

const asBoolean = (stored: unknown): boolean | undefined => (typeof stored === 'boolean' ? stored : undefined)

const asStringList = (stored: unknown): string[] | undefined => (isStringList(stored) ? stored : undefined)

const asNumberList = (stored: unknown): number[] | undefined => (isNumberList(stored) ? stored : undefined)

const asOneOf =
    <TValue extends string>(allowed: readonly TValue[]) =>
    (stored: unknown): TValue | undefined =>
        allowed.find((option) => option === stored)

/** Validated against the same list the select renders, so a size the board no longer offers is not restored. */
const asSchedulerPageSize = (stored: unknown): SchedulerPageSize | undefined => SCHEDULER_PAGE_SIZES.find((size) => size === stored)

/** `null` is a real value for these filters ("nothing selected"), not a missing one. */
const asNullable =
    <TValue>(parse: (stored: unknown) => TValue | undefined) =>
    (stored: unknown): TValue | null | undefined =>
        stored === null ? null : parse(stored)

const asStopTypes = (stored: unknown): StopTypeFilter[] | undefined => {
    if (!Array.isArray(stored)) return undefined
    const known = Object.values(StopTypeFilter).filter((option) => stored.includes(option))
    // Restoring [] would hide every row — the board spells "no stop-type filter" as [ALL].
    return known.length > 0 ? known : undefined
}

const asDateRange = (stored: unknown): AllTripsDateRange | undefined => {
    if (!isRecord(stored)) return undefined
    const startDate = asString(stored.startDate)
    const endDate = asString(stored.endDate)
    if (startDate === undefined || endDate === undefined) return undefined
    return stored.basis === undefined ? { startDate, endDate } : { startDate, endDate, basis: toTripDateBasis(stored.basis) }
}

const asSchedulerQuery = (stored: unknown): SchedulerQuery | undefined => {
    if (!isRecord(stored)) return undefined
    const search = asString(stored.search)
    const driver = asStringList(stored.driver)
    const dispatcher = asStringList(stored.dispatcher)
    const driverStatus = asStringList(stored.driverStatus)
    if (search === undefined || !driver || !dispatcher || !driverStatus) return undefined
    return { search, driver, dispatcher, driverStatus }
}

const asRcFilter = (stored: unknown): { nextWeek: boolean; endDate: boolean } | undefined => {
    if (!isRecord(stored)) return undefined
    const nextWeek = asBoolean(stored.nextWeek)
    const endDate = asBoolean(stored.endDate)
    return nextWeek !== undefined && endDate !== undefined ? { nextWeek, endDate } : undefined
}

// ── The boards ───────────────────────────────────────────────────────────────

const BROKER_FILTER_MODES = ['filter', 'priority'] as const
const LANE_FILTER_MODES = ['all', 'only', 'exclude'] as const
const FINANCE_STATUSES: readonly (LoadFinanceStatus | 'all')[] = ['all', ...Object.values(LoadFinanceStatus)]
const DRIVER_FILTERS = Object.values(DriverFilter)

/** The sort is deliberately absent — it has had its own persistence for longer, see ongoingLoadsSortPersistence. */
export const ONGOING_LOADS_BOARD: BoardFilterSpec = {
    storageKeyPrefix: 'ongoing-loads-filters',
    fields: [
        loadsFilter('dispatcherFilter', asStringList, (value) => setDispatcherFilter(value)),
        loadsFilter('tagFilter', asStringList, (value) => setTagFilter(value)),
        loadsFilter('driverFilter', asStringList, (value) => setDriverFilter(value)),
        loadsFilter('brokerFilter', asStringList, (value) => setBrokerFilter(value)),
        loadsFilter('brokerFilterMode', asOneOf(BROKER_FILTER_MODES), (value) => setBrokerFilterMode(value)),
        loadsFilter('laneFilterMode', asOneOf(LANE_FILTER_MODES), (value) => setLaneFilterMode(value)),
        loadsFilter('stopTypeFilter', asStopTypes, (value) => setStopTypeFilter(value)),
        loadsFilter('financeStatusFilter', asOneOf(FINANCE_STATUSES), (value) => setFinanceStatusFilter(value)),
        loadsFilter('attentionNeededFilter', asBoolean, (value) => setAttentionNeededFilter(value)),
        loadsFilter('unassignedRiskFilter', asNumberList, (value) => setUnassignedRiskFilter(value)),
        loadsFilter('selectedLoadGroupId', asNullable(asString), (value) => setSelectedLoadGroupId(value)),
        loadsFilter('searchQuery', asString, (value) => setSearchQuery(value)),
        loadsFilter('allTripsDateRange', asDateRange, (value) => setAllTripsDateRange(value)),
    ],
}

export const DISPATCH_BOARD: BoardFilterSpec = {
    storageKeyPrefix: 'dispatch-board-filters',
    fields: [
        // A restored search means this dispatcher already chose one, so SchedulerContent's "default
        // a dispatcher to their own trucks" effect must leave it alone. With nothing saved the flag
        // has to go back to false — otherwise the next person to log in on this browser inherits it
        // as true and never gets their own default applied.
        boardFilter('search', asSchedulerQuery, (value, fromStorage) => [setSearch(value), setDispatcherFilterInitialized(fromStorage)]),
        boardFilter('driverFilter', asOneOf(DRIVER_FILTERS), (value) => setBoardDriverFilter(value)),
        boardFilter('driverStatusFilter', asNullable(asString), (value) => setDriverStatusFilter(value)),
        boardFilter('rcFilter', asRcFilter, (value) => setRCFilter(value)),
        /**
         * Hand-written rather than a `boardFilter(...)` one-liner: that helper persists a whole slice field,
         * and `viewPagination` also carries `skip` and the server's `total`, neither of which is a preference.
         * Only the page size is. The default still comes from the slice, not a literal, so it cannot drift.
         */
        {
            name: 'viewPaginationLimit',
            read: (state) => state.scheduler.viewPagination.limit,
            restore: (stored, dispatch) => {
                dispatch(
                    setViewPagination({
                        skip: 0,
                        limit: asSchedulerPageSize(stored) ?? DEFAULT_BOARD_STATE.scheduler.viewPagination.limit,
                        total: 0,
                    })
                )
            },
        },
    ],
}

/**
 * The boards before anyone touches them — what "not filtered" means for every field above.
 * Saving is diffed against this so an entry only ever holds filters the user actually chose:
 * a company back at its defaults stores nothing at all, rather than a full set of default values.
 */
export const DEFAULT_BOARD_STATE: BoardFilterState = {
    loads: loadsReducer(undefined, { type: '@@boardFilters/defaults' }),
    scheduler: schedulerReducer(undefined, { type: '@@boardFilters/defaults' }),
}
